import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Flex,
    IconButton,
    Switch,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';
import PageTransition from '../../../components/animations/PageTransition';
import SkeletonTable from '../../../components/states/SkeletonTable';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { translate } from '../../../i18n';
import { tableHeaderTextProps, tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps } from '../../../theme/constants';
import { AchievementDefinitionDto } from '../../../models/achievement/dto/achievement.dto';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useDeleteAchievement } from '../hooks/useDeleteAchievement';
import { useListAchievements } from '../hooks/useListAchievements';
import { useToggleAchievement } from '../hooks/useToggleAchievement';
import CreateAchievementModal from './CreateAchievementModal';

const TIER_COLORS: Record<string, string> = {
    BRONZE: 'orange',
    SILVER: 'gray',
    GOLD: 'yellow',
    PLATINUM: 'purple',
};

const columnHelper = createColumnHelper<AchievementDefinitionDto>();

export default function AchievementListPage() {
    const { selectedOrganization } = useSelectedOrganization();
    // `useListAchievements` is `enabled: !!organizationId`. This page DID have
    // a scope guard — and it was fused to the loading arm
    // (`if (isLoading || !selectedOrganization) return <SkeletonTable/>`), so
    // the guard rendered the defect instead of preventing it: an operator with
    // no organization selected got a six-row skeleton table forever.
    //
    // Worth stating because it is a third shape, and the one a census cannot
    // see: run 104's fail-safe files early-return to a PROMPT and run 105's
    // broken ones have no guard at all, so "does this file test
    // `!selectedOrganization`?" classifies this one as safe. What matters is
    // which affordance the guard renders, not that it exists.
    const { data, isInitialLoading, isError } = useListAchievements(selectedOrganization?.id || '');
    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const toast = useToast();
    const navigate = useNavigate();

    // Create modal
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();

    // Delete dialog
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [deletingAchievement, setDeletingAchievement] = useState<AchievementDefinitionDto | null>(null);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const { mutateAsync: deleteAchievement, isLoading: isDeleting } = useDeleteAchievement();

    // Toggle
    const { mutateAsync: toggleAchievement } = useToggleAchievement();
    const [togglingId, setTogglingId] = useState<string | null>(null);

    const handleDelete = (achievement: AchievementDefinitionDto) => {
        setDeletingAchievement(achievement);
        onDeleteOpen();
    };

    const handleConfirmDelete = async () => {
        if (!deletingAchievement) return;
        try {
            await deleteAchievement(deletingAchievement.id);
            toast({
                title: translate('achievement.deleted_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error: any) {
            toast({
                title: translate('achievement.delete_error'),
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            onDeleteClose();
            setDeletingAchievement(null);
        }
    };

    const handleToggle = useCallback(async (achievement: AchievementDefinitionDto) => {
        setTogglingId(achievement.id);
        try {
            await toggleAchievement(achievement.id);
        } catch (error: any) {
            toast({
                title: translate('achievement.toggle_error'),
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setTogglingId(null);
        }
    }, [toggleAchievement, toast]);

    const columns = useMemo(() => [
        columnHelper.accessor('name', {
            id: 'name',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('achievement.name')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize='sm' fontWeight='700'>
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('category', {
            id: 'category',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('achievement.category')}
                </Text>
            ),
            cell: (info) => (
                <Badge colorScheme="brand" fontSize="xs">
                    {info.getValue()}
                </Badge>
            ),
        }),
        columnHelper.accessor('tier', {
            id: 'tier',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('achievement.tier')}
                </Text>
            ),
            cell: (info) => {
                const tier = info.getValue();
                if (!tier) return <Text color="gray.400" fontSize="sm">—</Text>;
                return (
                    <Badge colorScheme={TIER_COLORS[tier] || 'gray'} fontSize="xs">
                        {tier}
                    </Badge>
                );
            },
        }),
        columnHelper.accessor('criteria', {
            id: 'criteria',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('achievement.criteria')}
                </Text>
            ),
            cell: (info) => {
                const criteria = info.getValue();
                return (
                    <Text color={textColor} fontSize='sm'>
                        {criteria?.type?.replace(/_/g, ' ') ?? '—'} ≥ {criteria?.threshold ?? '?'}
                        {criteria?.sport ? ` (${criteria.sport})` : ''}
                    </Text>
                );
            },
        }),
        columnHelper.accessor('isActive', {
            id: 'isActive',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('achievement.active')}
                </Text>
            ),
            cell: (info) => (
                <Switch
                    isChecked={info.getValue()}
                    isDisabled={togglingId === info.row.original.id}
                    onChange={(e) => {
                        e.stopPropagation();
                        handleToggle(info.row.original);
                    }}
                    colorScheme="brand"
                    size="sm"
                />
            ),
        }),
        columnHelper.accessor('sortOrder', {
            id: 'sortOrder',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('achievement.sort_order')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize='sm'>
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('achievement.actions')}
                </Text>
            ),
            cell: (info) => (
                <Flex align='center' gap={2}>
                    <IconButton
                        aria-label={translate('achievement.edit_achievement')}
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="brand"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/achievements/${info.row.original.id}`);
                        }}
                    />
                    <IconButton
                        aria-label={translate('achievement.delete_achievement')}
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(info.row.original);
                        }}
                    />
                </Flex>
            ),
        }),
    ], [textColor, handleToggle, togglingId, navigate]);

    const table = useReactTable({
        data: data || [],
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const readState = classifyRead(isError, isInitialLoading, data);

    if (readState === 'never-issued') {
        return (
            <PageCard title={translate('achievement.achievements')}>
                <ScopePrompt gate='organization' />
            </PageCard>
        );
    }

    if (readState === 'loading') {
        return (
            <PageCard title={translate('achievement.achievements')}>
                <SkeletonTable rows={6} columns={7} />
            </PageCard>
        );
    }

    // `unreadable`, not the raw `isError`: a background refetch that fails
    // over rows already on screen keeps them rather than replacing the whole
    // table with an alert (R96-3).
    if (readState === 'unreadable') {
        return (
            <PageCard title={translate('achievement.achievements')}>
                <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    {translate('achievement.load_error')}
                </Alert>
            </PageCard>
        );
    }

    return (
        <PageTransition>
        <PageCard
            title={translate('achievement.achievements')}
            rightHeader={
                <Flex align="center" gap={3}>
                    {data && data.length > 0 && (
                        <Text fontSize="xs" color="gray.500">{data.length} {translate('achievement.achievements').toLowerCase()}</Text>
                    )}
                    <Button colorScheme="brand" onClick={onCreateOpen} size="sm">
                        {translate('achievement.create_achievement')}
                    </Button>
                </Flex>
            }
        >
            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <Th
                                        {...tableThProps}
                                        key={header.id}
                                        borderColor={borderColor}
                                        cursor='pointer'
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <Flex
                                            {...tableThFlexProps}>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            <SortIndicator direction={header.column.getIsSorted()} />
                                        </Flex>
                                    </Th>
                                ))}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map(row => (
                            <Tr
                                key={row.id}
                                {...tableRowProps}
                                onClick={() => navigate(`/admin/achievements/${row.original.id}`)}
                            >
                                {row.getVisibleCells().map(cell => (
                                    <Td
                                        key={cell.id}
                                        {...tableCellProps}
                                        borderColor={borderColor}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Td>
                                ))}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
                {(!data || data.length === 0) && (
                    <Flex justify="center" py={8}>
                        <Text color="gray.400">{translate('achievement.no_achievements')}</Text>
                    </Flex>
                )}
            </Box>

            <CreateAchievementModal isOpen={isCreateOpen} onClose={onCreateClose} />

            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelButtonRef}
                onClose={onDeleteClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                            {translate('achievement.delete_achievement')}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {translate('achievement.delete_confirm').replace('{name}', deletingAchievement?.name || '')}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelButtonRef} onClick={onDeleteClose}>
                                {translate('common.cancel')}
                            </Button>
                            <Button colorScheme='red' onClick={handleConfirmDelete} ml={3} isLoading={isDeleting}>
                                {translate('common.delete')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </PageCard>
        </PageTransition>
    );
}
